import { Button } from 'components'
import { useRouter } from 'next/router'
import React, { Fragment, useMemo } from 'react'
import {
  setLoginModalOpen,
  useAlgoEditorState,
  useAppDispatch,
  useLoginState
} from 'state'
import { useWindowSize } from 'usehooks-ts'

import { Menu, Transition } from '@headlessui/react'
import { XIcon } from '@heroicons/react/outline'
import { TokensIcon } from '@radix-ui/react-icons'
import { tx } from '@twind/core'

import { AlgoChallenge } from '../AlgoEditor.types'
import { AlgoEditorTabs } from '../data'

import LoginEditorModal from './LoginEditorModal'

const QuestionSection = ({ question }: { question: AlgoChallenge }) => {
  const router = useRouter()
  const { tab } = router.query

  const selectedTabKey =
    typeof tab === 'string' ? tab || 'question' : 'question'

  // const discussionSlug = typeof uniqueId === 'string' ? uniqueId : ''

  const loginState = useLoginState()
  // const [selectedTabKey, setSelectedTabKey] = useState('question')
  const dispatch = useAppDispatch()
  const { editorWidth } = useAlgoEditorState()
  const { width: windowWidth } = useWindowSize()

  const selectedTab = useMemo(() => {
    return AlgoEditorTabs.find(({ key }) => key === selectedTabKey)
  }, [selectedTabKey])

  if (!selectedTab) {
    return (
      <div className="font-bold text-4xl flex justify-center items-center min-h-screen">
        oops! The requested Tab does not exist...
      </div>
    )
  }

  const handleOpenTabs = (key: string) => {
    if (
      ['submissions', 'discussions'].includes(key) &&
      !loginState.isLoggedIn
    ) {
      dispatch(setLoginModalOpen(true))
    } else {
      return router.push({
        query: {
          ...router.query,
          tab: key
        }
      })
    }
  }

  return (
    <>
      <div className="px-2">
        <p className="bg-primary px-2.5 py-1 rounded text-center block md:hidden w-full">
          To solve this problem, you&apos;ll have to open it on the computer
        </p>
      </div>

      <div className="z-20 relative border-b-1 border-zinc-700">
        {editorWidth > 54 || windowWidth < 1270 ? (
          <Menu>
            {({ open }) => (
              <div className="relative flex justify-end mx-3 my-1">
                <Menu.Button className="flex gap-2 items-center hover:bg-white hover:bg-opacity-10 text-white px-3 py-2 rounded-md text-base font-medium transition-colors focus:outline-none focus:ring">
                  Tabs{' '}
                  {open ? (
                    <XIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <TokensIcon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95">
                  <Menu.Items className="flex flex-col gap-1 absolute right-0 rounded-md shadow-lg bg-zinc-700 top-10 px-1 py-1">
                    {AlgoEditorTabs.map((item) => (
                      <Menu.Item key={item.key}>
                        <div
                          onClick={() => handleOpenTabs(item.key)}
                          className={tx(
                            'min-w-[15rem] cursor-pointer flex items-center font-medium rounded px-3 py-1.5',
                            selectedTabKey === item.key
                              ? 'bg-primary'
                              : 'hover:bg-zinc-600',
                            item.key === 'excalidraw' && 'hidden lg:flex'
                          )}>
                          {<item.icon />}
                          <span className="ml-2">{item.label}</span>
                        </div>
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </div>
            )}
          </Menu>
        ) : (
          <nav className="w-full mx-auto lg:max-w-7xl flex items-center px-3 py-1.5 space-x-2 w-full mx-auto">
            {AlgoEditorTabs.map((item) => (
              <Button
                key={item.key}
                variant="transparent"
                onClick={() => handleOpenTabs(item.key)}
                className={tx(
                  'flex items-center text-sm font-medium rounded-md bg-white px-2 py-1.5',
                  selectedTabKey === item.key
                    ? 'text-white bg-opacity-10'
                    : 'bg-opacity-0'
                )}>
                {<item.icon />}
                <span className="ml-2">{item.label}</span>
              </Button>
            ))}
          </nav>
        )}
      </div>

      {question && (
        <main className="w-full pt-3 px-4 flex-1">
          {selectedTab ? (
            <selectedTab.component
              question={question}
              questionId={question.id}
              questionType={'Challenge'}
            />
          ) : (
            'No tab'
          )}
        </main>
      )}

      <LoginEditorModal />
    </>
  )
}

export default QuestionSection
